import type { ModuloVendible } from "@/lib/billing/precios"
import type { AreaDeModulo } from "@/lib/modules/areas"

/**
 * Lo que la web pública cuenta de cada módulo: una ficha por módulo vendible.
 *
 * **De aquí salen las 21 páginas `/modulos/[slug]`, el catálogo, la portada y
 * la calculadora.** Antes cada ficha era una página de 700 a 870 líneas hecha a
 * mano, y solo había siete. Una página nueva es una entrada más aquí.
 *
 * **Lo que se cuenta es lo que hay.** Cada texto sale de las pantallas reales
 * del módulo (spec 2026-09-12, catálogo de módulos). Nada de cifras de
 * clientes ni funciones por venir: si algo no está, no se escribe.
 *
 * El precio NO está aquí: se calcula con `lib/billing/factura-de-modulos.ts`,
 * que es lo mismo que se cobra.
 */

export interface Situacion {
  antes: string
  ahora: string
  texto: string
}

export interface Funcion {
  titulo: string
  texto: string
}

export interface Conexion {
  modulo: ModuloVendible
  texto: string
}

export interface MaticeDeSector {
  sector: SectorDeLanding
  texto: string
}

export interface Pregunta {
  pregunta: string
  respuesta: string
}

export interface FichaDeModulo {
  clave: ModuloVendible
  slug: string
  nombre: string
  /** Una línea, para listas y tarjetas. */
  resumen: string
  /** El titular de su página: el problema resuelto, no el nombre del módulo. */
  titular: string
  entradilla: string
  /** Lo que deja de pasar. Tres. */
  situaciones: Situacion[]
  funciones: Funcion[]
  conexiones: Conexion[]
  sectores: MaticeDeSector[]
  preguntas: Pregunta[]
  /** Si necesita otro módulo para funcionar. */
  requiere?: ModuloVendible
}

export type SectorDeLanding = "hosteleria" | "tienda" | "ecommerce" | "servicios" | "saas" | "otros"

export const NOMBRE_DE_AREA: Record<AreaDeModulo, string> = {
  equipo: "Equipo",
  operaciones: "Operaciones",
  ventas: "Ventas",
  direccion: "Dirección",
}

export const DESCRIPCION_DE_AREA: Record<AreaDeModulo, string> = {
  equipo: "Fichaje, turnos, ausencias, nóminas y todo lo de las personas.",
  operaciones: "Stock, compras, producción, gastos y banco.",
  ventas: "Facturas, caja, sala y cartas, reservas.",
  direccion: "Informes, chat y proyectos: ver y decidir.",
}

export const AREAS_EN_ORDEN: AreaDeModulo[] = ["equipo", "operaciones", "ventas", "direccion"]

export interface Sector {
  clave: SectorDeLanding
  nombre: string
  /** Como lo describe el alta. */
  descripcion: string
}

export const SECTORES: Sector[] = [
  { clave: "hosteleria", nombre: "Hostelería", descripcion: "Restaurantes, bares, cafeterías, hoteles" },
  { clave: "tienda", nombre: "Tienda", descripcion: "Comercio con local físico" },
  { clave: "ecommerce", nombre: "Ecommerce", descripcion: "Venta online" },
  { clave: "servicios", nombre: "Servicios", descripcion: "Asesorías, agencias, consultoras" },
  { clave: "saas", nombre: "Software", descripcion: "Software por suscripción" },
  { clave: "otros", nombre: "Otro", descripcion: "Cualquier otro negocio" },
]

/**
 * Los módulos que trae marcados cada tipo de negocio (spec 2026-09-12, presets).
 *
 * Es una copia de `business_type_modules` para poder pintarlo sin base de datos
 * en una página estática. El alta usa la tabla; si cambia allí, cambia aquí.
 */
const DE_TODOS: ModuloVendible[] = [
  "fichaje", "ausencias", "nominas", "documentos", "sugerencias", "facturacion", "gastos", "banca", "informes",
]
export const PRESET_DE_SECTOR: Record<SectorDeLanding, ModuloVendible[]> = {
  hosteleria: [...DE_TODOS, "turnos", "tareas", "tickets", "inventario", "compras", "producciones", "cartas_pedidos", "reservas"],
  tienda: [...DE_TODOS, "turnos", "tareas", "tickets", "inventario", "compras"],
  ecommerce: [...DE_TODOS, "inventario", "compras", "producciones", "workforce", "chat", "proyectos"],
  servicios: [...DE_TODOS, "chat", "proyectos"],
  saas: [...DE_TODOS, "chat", "proyectos"],
  otros: [...DE_TODOS, "chat", "proyectos"],
}

export const MODULOS: FichaDeModulo[] = [
  // ── Equipo ──────────────────────────────────────────────────────────────
  {
    clave: "fichaje",
    slug: "fichaje",
    nombre: "Fichaje",
    resumen: "Entradas, pausas y salidas desde el móvil, una tablet o el ordenador. El registro que pide la ley.",
    titular: "El registro de jornada, hecho al entrar por la puerta.",
    entradilla:
      "Cada persona ficha desde el móvil, una tablet en la entrada o el ordenador. Tú tienes el registro que pide la ley, las horas de cada uno y quién está trabajando ahora mismo.",
    situaciones: [
      { antes: "La hoja de firmas en el mostrador", ahora: "El registro diario que pide la ley, guardado", texto: "Cada entrada, pausa y salida con su hora y desde dónde se hizo." },
      { antes: "«¿Quién ha venido hoy?»", ahora: "Quién está trabajando, en tiempo real", texto: "Por centro, con aviso de quien hace horas de más." },
      { antes: "Sumar horas a fin de mes", ahora: "Las horas de cada uno, ya sumadas", texto: "Por día, semana y mes, y comparadas con su turno si usas Turnos." },
    ],
    funciones: [
      { titulo: "Desde el móvil", texto: "La app de Zindra en iPhone y Android: un botón para entrar y otro para la pausa." },
      { titulo: "En una tablet", texto: "Un kiosko en la entrada, con tarjeta o con un código de cuatro dígitos." },
      { titulo: "Desde el ordenador", texto: "En Zindra Work, la app de escritorio, sin abrir nada más." },
      { titulo: "Por centro", texto: "Cada centro con su zona: se ficha estando en él." },
    ],
    conexiones: [
      { modulo: "turnos", texto: "Compara lo fichado con lo planificado: retrasos, horas de más y huecos." },
      { modulo: "nominas", texto: "Las horas del mes, a mano para hacer la nómina." },
      { modulo: "teletrabajo", texto: "Mientras se está fichado en remoto, el tiempo y las aplicaciones." },
      { modulo: "workforce", texto: "Qué hizo cada uno en sus horas y cuánto sale por hora." },
      { modulo: "informes", texto: "Horas por centro y persona, junto a las ventas del día." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Turnos partidos y una tablet en la barra para fichar sin sacar el móvil." },
      { sector: "servicios", texto: "Se ficha desde el ordenador, también en remoto." },
      { sector: "ecommerce", texto: "Fichaje por puesto en el almacén, con la producción por hora." },
    ],
    preguntas: [
      { pregunta: "¿Es obligatorio registrar la jornada?", respuesta: "Sí. En España el registro diario de jornada es obligatorio desde 2019 y hay que conservarlo cuatro años." },
      { pregunta: "¿Necesito comprar un aparato?", respuesta: "No. Vale el móvil de cada persona, cualquier tablet como kiosko o el ordenador." },
      { pregunta: "¿Puedo empezar solo con Fichaje?", respuesta: "Sí: el núcleo y Fichaje. Turnos, Ausencias o Nóminas se añaden después, con lo fichado ya dentro." },
    ],
  },
  {
    clave: "turnos",
    slug: "turnos",
    nombre: "Turnos",
    resumen: "Cuadrantes por centro y puesto, publicados al móvil de cada uno.",
    titular: "El cuadrante de la semana, sin hoja de cálculo ni grupo de WhatsApp.",
    entradilla:
      "Montas los turnos por centro y puesto, Zindra te avisa de quién falta o se solapa con unas vacaciones, y cada persona ve los suyos en el móvil.",
    situaciones: [
      { antes: "El Excel del cuadrante, en cinco versiones", ahora: "Un cuadrante, el mismo para todos", texto: "Publicado al móvil de cada persona en cuanto lo cierras." },
      { antes: "Descubrir el lunes que alguien estaba de vacaciones", ahora: "Los solapes, avisados al montar", texto: "Con Ausencias, el cuadrante sabe quién no está." },
      { antes: "Hacer la semana desde cero", ahora: "Patrones que se repiten", texto: "Tipos de turno, patrones y necesidades de personal por día." },
    ],
    funciones: [
      { titulo: "Cuadrante por centro", texto: "Semana a semana, por puesto, con quién cubre cada hueco." },
      { titulo: "Patrones y tipos de turno", texto: "Mañana, tarde, partido: se definen una vez y se aplican." },
      { titulo: "Preferencias y necesidades", texto: "Cuánta gente hace falta cada día y qué prefiere cada uno." },
      { titulo: "Propuesta con IA", texto: "Un primer cuadrante a partir de necesidades y preferencias, que luego ajustas." },
    ],
    conexiones: [
      { modulo: "ausencias", texto: "Vacaciones y bajas aparecen en el cuadrante y avisan de solapes." },
      { modulo: "fichaje", texto: "Lo planificado frente a lo fichado." },
      { modulo: "tareas", texto: "Las tareas se asignan por turno: apertura, cierre, limpieza." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Turnos partidos, refuerzos de fin de semana y varios locales." },
      { sector: "tienda", texto: "Apertura y cierre de cada tienda, con quien cubre cada franja." },
    ],
    preguntas: [
      { pregunta: "¿Cada persona ve sus turnos?", respuesta: "Sí, en la app del móvil, en cuanto se publica el cuadrante." },
      { pregunta: "¿Sirve con varios centros?", respuesta: "Sí. Cada centro tiene su cuadrante y se ven juntos o por separado." },
    ],
  },
  {
    clave: "ausencias",
    slug: "ausencias",
    nombre: "Ausencias",
    resumen: "Vacaciones y bajas con su aprobación, sus saldos y el calendario del equipo.",
    titular: "Vacaciones pedidas, aprobadas y en el calendario, sin un solo correo.",
    entradilla:
      "Cada persona pide sus días desde el móvil, el responsable los aprueba y quedan en el calendario del equipo, con el saldo de cada uno al día.",
    situaciones: [
      { antes: "Pedir vacaciones por correo", ahora: "Se piden y se aprueban en la app", texto: "Con aviso a quien tiene que aprobar." },
      { antes: "«¿Cuántos días me quedan?»", ahora: "El saldo de cada uno, a la vista", texto: "Según la política de la empresa." },
      { antes: "Tres personas fuera la misma semana", ahora: "El calendario del equipo, de un vistazo", texto: "Antes de aprobar, se ve quién más falta." },
    ],
    funciones: [
      { titulo: "Solicitud y aprobación", texto: "Vacaciones, asuntos propios y bajas, con su flujo." },
      { titulo: "Saldos y políticas", texto: "Días por año, arrastre y tipos de ausencia." },
      { titulo: "Calendario de equipo", texto: "Quién está fuera cada día, por departamento." },
      { titulo: "En el móvil", texto: "Pedir y aprobar desde la app." },
    ],
    conexiones: [
      { modulo: "turnos", texto: "Las ausencias aparecen en el cuadrante y avisan de solapes." },
      { modulo: "fichaje", texto: "Un día de vacaciones no cuenta como falta de fichaje." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Temporada alta bloqueada, para no aprobar lo que no se puede cubrir." },
      { sector: "servicios", texto: "El calendario del equipo para repartir el trabajo con quien está." },
    ],
    preguntas: [
      { pregunta: "¿Puedo tener tipos de ausencia propios?", respuesta: "Sí: vacaciones, asuntos propios, bajas y los que definas." },
      { pregunta: "¿Quién aprueba?", respuesta: "Quien tenga permiso para gestionar ausencias en tu empresa." },
    ],
  },
  {
    clave: "nominas",
    slug: "nominas",
    nombre: "Nóminas",
    resumen: "Las nóminas de cada mes repartidas a cada persona, y el coste de la plantilla.",
    titular: "Cada nómina en su sitio y el coste de tu plantilla a la vista.",
    entradilla:
      "Subes las nóminas del mes de una vez y Zindra las reparte a cada persona, que la tiene en su móvil. Tú ves cuánto cuesta la plantilla, con finiquitos incluidos.",
    situaciones: [
      { antes: "Enviar las nóminas una a una", ahora: "Se suben todas a la vez y se reparten", texto: "La IA reconoce de quién es cada una." },
      { antes: "«¿Me reenvías la nómina de marzo?»", ahora: "Cada persona tiene las suyas", texto: "En la app, cuando las necesite." },
      { antes: "El coste de personal, en el gestor", ahora: "El coste de la plantilla, en Zindra", texto: "Junto a las ventas y los gastos." },
    ],
    funciones: [
      { titulo: "Subida masiva", texto: "Un archivo con todas las nóminas; cada una va a su persona." },
      { titulo: "Mis nóminas", texto: "Cada empleado ve y descarga las suyas." },
      { titulo: "Finiquitos", texto: "También en su sitio." },
      { titulo: "Coste de plantilla", texto: "Lo que cuesta el equipo, mes a mes." },
    ],
    conexiones: [
      { modulo: "gastos", texto: "La nómina cuenta como gasto de la empresa." },
      { modulo: "informes", texto: "Coste de personal frente a ventas." },
      { modulo: "fichaje", texto: "Las horas del mes, a mano para el cálculo." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Plantillas que cambian cada temporada, sin perder ninguna nómina." },
      { sector: "servicios", texto: "El coste del equipo junto a lo facturado." },
    ],
    preguntas: [
      { pregunta: "¿Zindra calcula las nóminas?", respuesta: "Las nóminas las hace tu gestoría o tu programa; Zindra las reparte, las guarda y te da el coste." },
      { pregunta: "¿Cada empleado ve solo las suyas?", respuesta: "Sí. Cada persona ve solo sus documentos." },
    ],
  },
  {
    clave: "documentos",
    slug: "documentos",
    nombre: "Documentos",
    resumen: "Contratos y documentos de cada persona, por categorías y con firma.",
    titular: "Los papeles de cada persona, firmados y en un solo sitio.",
    entradilla: "Contratos, anexos, protocolos: cada documento en la ficha de su persona, por categorías, y con su firma.",
    situaciones: [
      { antes: "Contratos en una carpeta compartida", ahora: "Cada documento en la ficha de su persona", texto: "Por categorías." },
      { antes: "Imprimir, firmar, escanear", ahora: "Se firma en Zindra", texto: "Y queda guardado." },
      { antes: "«¿Me pasas mi contrato?»", ahora: "Cada persona tiene los suyos", texto: "En la app." },
    ],
    funciones: [
      { titulo: "Por persona y categoría", texto: "Contratos, formación, protocolos, lo que necesites." },
      { titulo: "Firma", texto: "Se pide la firma y queda registrada." },
      { titulo: "Mis documentos", texto: "Cada empleado ve los suyos." },
    ],
    conexiones: [{ modulo: "nominas", texto: "Nóminas y documentos, en la misma ficha." }],
    sectores: [{ sector: "hosteleria", texto: "Protocolos de higiene y formación firmados por cada persona." }],
    preguntas: [{ pregunta: "¿Puedo traer los documentos de Factorial?", respuesta: "Sí, el conector de Factorial trae los documentos si tienes este módulo." }],
  },
  {
    clave: "tareas",
    slug: "tareas",
    nombre: "Tareas",
    resumen: "Listas de apertura, cierre y limpieza por turno, con quién las hizo.",
    titular: "Apertura, cierre y limpieza, hechas y con su firma.",
    entradilla: "Checklists por turno o por persona, que se marcan desde el móvil. Sabes qué se hizo, quién lo hizo y cuándo, y lo exportas para el APPCC.",
    situaciones: [
      { antes: "La lista pegada en la nevera", ahora: "La lista en el móvil del turno", texto: "Cada punto marcado con su hora." },
      { antes: "«¿Alguien ha limpiado la cámara?»", ahora: "Quién lo hizo y cuándo", texto: "Sin preguntar." },
      { antes: "Preparar el APPCC para una inspección", ahora: "Se exporta", texto: "Con el registro de cada día." },
    ],
    funciones: [
      { titulo: "Plantillas", texto: "Las listas de siempre, definidas una vez." },
      { titulo: "Por turno o por persona", texto: "Con Turnos, al turno; sin él, a personas y departamentos." },
      { titulo: "Etiquetas", texto: "Para ordenar por zona o tipo." },
      { titulo: "Exportación APPCC", texto: "El registro, listo para quien lo pida." },
    ],
    conexiones: [{ modulo: "turnos", texto: "Las tareas se asignan al turno que toca." }],
    sectores: [
      { sector: "hosteleria", texto: "APPCC, temperaturas y limpieza, con su registro." },
      { sector: "tienda", texto: "Apertura y cierre de cada tienda." },
    ],
    preguntas: [{ pregunta: "¿Funciona sin Turnos?", respuesta: "Sí. Sin Turnos, las tareas se asignan a personas o departamentos." }],
  },
  {
    clave: "sugerencias",
    slug: "comunicacion",
    nombre: "Comunicación",
    resumen: "Noticias de la empresa y buzón de sugerencias.",
    titular: "Lo que tiene que saber todo el equipo, y lo que el equipo quiere decir.",
    entradilla: "Publica noticias para toda la empresa y abre un buzón de sugerencias, también anónimas, que llega a quien tiene que leerlo.",
    situaciones: [
      { antes: "El aviso que no leyó nadie", ahora: "Noticias en la app de cada uno", texto: "Para toda la empresa o un departamento." },
      { antes: "Las quejas en el pasillo", ahora: "Un buzón de sugerencias", texto: "Que llega a quien decide." },
      { antes: "«Nadie me dijo nada»", ahora: "Queda publicado", texto: "Con su fecha." },
    ],
    funciones: [
      { titulo: "Noticias", texto: "Publicaciones para toda la empresa, en la app." },
      { titulo: "Buzón de sugerencias", texto: "Ideas y quejas del equipo, con su respuesta." },
    ],
    conexiones: [{ modulo: "chat", texto: "Para el día a día, el chat; para lo que tiene que quedar, Comunicación." }],
    sectores: [{ sector: "hosteleria", texto: "Avisos para quien no tiene ordenador, en su móvil." }],
    preguntas: [{ pregunta: "¿Las sugerencias pueden ser anónimas?", respuesta: "Sí, quien escribe elige." }],
  },
  {
    clave: "workforce",
    slug: "workforce",
    nombre: "Workforce",
    resumen: "Trabajos, puestos y producción por hora, con kiosko en planta.",
    titular: "Qué hace cada persona en sus horas, y cuánto sale de cada una.",
    entradilla: "Cada persona marca en qué trabajo está desde el kiosko de planta. Ves el rendimiento por puesto y por hora, y el cuadrante del día.",
    requiere: "fichaje",
    situaciones: [
      { antes: "Saber cuánta gente hay, no qué hace", ahora: "Quién está en cada trabajo", texto: "Desde el kiosko de planta." },
      { antes: "La productividad, a ojo", ahora: "Unidades por hora y por persona", texto: "Con lo que sale de producción y compras." },
      { antes: "Repartir puestos en una pizarra", ahora: "El cuadrante del día", texto: "Por puestos y trabajos." },
    ],
    funciones: [
      { titulo: "Kiosko de planta", texto: "Una tablet para marcar en qué trabajo se está." },
      { titulo: "Catálogo de trabajos", texto: "Preparación, empaquetado, envíos: lo que se hace." },
      { titulo: "Rendimiento", texto: "Unidades por hora, por puesto y persona." },
      { titulo: "Cuadrante", texto: "Quién va a cada puesto cada día." },
    ],
    conexiones: [
      { modulo: "fichaje", texto: "Workforce parte de lo fichado." },
      { modulo: "producciones", texto: "Las fases de producción usan su catálogo de trabajos." },
      { modulo: "compras", texto: "Las unidades previstas salen de los documentos." },
    ],
    sectores: [{ sector: "ecommerce", texto: "Preparación de pedidos y envíos medidos por hora." }],
    preguntas: [{ pregunta: "¿Necesita Fichaje?", respuesta: "Sí, Workforce reparte las horas fichadas entre trabajos." }],
  },
  {
    clave: "teletrabajo",
    slug: "teletrabajo",
    nombre: "Teletrabajo",
    resumen: "Actividad real en teletrabajo —tiempo, aplicaciones y webs— atada al fichaje.",
    titular: "El teletrabajo registrado, solo mientras se está trabajando.",
    entradilla: "Mientras alguien está fichado en remoto, la app de escritorio registra su tiempo activo, las aplicaciones y las webs. Fuera de la jornada, nada.",
    situaciones: [
      { antes: "El remoto, a ciegas", ahora: "El tiempo activo de cada jornada", texto: "Atado al fichaje." },
      { antes: "Pedir capturas o informes a mano", ahora: "Las aplicaciones y webs usadas", texto: "Por tramos." },
      { antes: "Controlar fuera de horario", ahora: "Solo dentro de la jornada", texto: "Al salir, deja de registrar." },
    ],
    funciones: [
      { titulo: "App de escritorio", texto: "Zindra Work para Mac y Windows registra mientras se está fichado." },
      { titulo: "Tiempo activo", texto: "Por día y persona." },
      { titulo: "Aplicaciones y webs", texto: "En qué se ha trabajado, por tramos." },
    ],
    conexiones: [{ modulo: "fichaje", texto: "Solo registra mientras la persona está fichada en remoto." }],
    sectores: [
      { sector: "saas", texto: "Equipos en remoto con su jornada registrada." },
      { sector: "servicios", texto: "Horas reales por persona en una agencia o asesoría." },
    ],
    preguntas: [
      { pregunta: "¿Registra fuera de la jornada?", respuesta: "No. Solo mientras la persona está fichada en remoto." },
      { pregunta: "¿En qué ordenadores funciona?", respuesta: "Con la app de Zindra Work para Mac y Windows." },
    ],
  },

  // ── Operaciones ─────────────────────────────────────────────────────────
  {
    clave: "inventario",
    slug: "inventario",
    nombre: "Inventario",
    resumen: "Stock por almacén, con movimientos, recuentos, mermas, lotes y caducidades.",
    titular: "El stock que dice Zindra es el que hay en el almacén.",
    entradilla: "Cada entrada y salida queda registrada: lo que se compra, lo que se produce y lo que se vende. Con recuentos, mermas, lotes, caducidades y alertas.",
    situaciones: [
      { antes: "El recuento que nunca cuadra", ahora: "Cada movimiento, registrado", texto: "Compras, producción y ventas mueven el stock solos." },
      { antes: "Descubrir la rotura de stock al servir", ahora: "Alertas de mínimo", texto: "Antes de quedarte sin nada." },
      { antes: "«¿De qué lote era?»", ahora: "Trazabilidad", texto: "Lotes y caducidades, de la compra a la venta." },
    ],
    funciones: [
      { titulo: "Almacenes", texto: "Stock por almacén y por centro." },
      { titulo: "Recuentos y mermas", texto: "Con su diferencia y su motivo, también desde el móvil." },
      { titulo: "Lotes y caducidades", texto: "Con la trazabilidad completa." },
      { titulo: "Códigos y etiquetas", texto: "Códigos de barras para contar y mover más rápido." },
    ],
    conexiones: [
      { modulo: "compras", texto: "Lo recibido entra en el stock." },
      { modulo: "producciones", texto: "Lo que se consume sale; lo producido entra." },
      { modulo: "facturacion", texto: "Lo facturado se descuenta." },
      { modulo: "tickets", texto: "Lo vendido en caja se descuenta." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Cámaras, barra y almacén, con mermas y caducidades." },
      { sector: "ecommerce", texto: "El mismo stock para Shopify, el almacén y los mayoristas." },
      { sector: "tienda", texto: "Stock por tienda y transferencias entre ellas." },
    ],
    preguntas: [
      { pregunta: "¿Se conecta con Shopify?", respuesta: "Sí. Shopify y ShipHero escriben en el stock de Zindra." },
      { pregunta: "¿Puedo contar desde el móvil?", respuesta: "Sí, los recuentos se hacen desde la app." },
    ],
  },
  {
    clave: "compras",
    slug: "compras",
    nombre: "Compras",
    resumen: "Pedidos a proveedor, recepciones y el histórico de precios de cada artículo.",
    titular: "Lo que pides, lo que llega y lo que te cuesta de verdad.",
    entradilla: "Órdenes de compra, albaranes de proveedor y recepción contra lo pedido. Cada compra deja su precio, y ese precio llega al coste de lo que produces.",
    situaciones: [
      { antes: "Pedir por WhatsApp al proveedor", ahora: "Órdenes de compra", texto: "Con lo pedido, a quién y para cuándo." },
      { antes: "Aceptar el albarán sin mirar", ahora: "Recepción contra lo pedido", texto: "Las diferencias, a la vista." },
      { antes: "No saber cuánto subió el precio", ahora: "El histórico de precios", texto: "De cada artículo y proveedor." },
    ],
    funciones: [
      { titulo: "Órdenes de compra", texto: "A cada proveedor, con su estado." },
      { titulo: "Albaranes y recepción", texto: "Lo recibido frente a lo pedido." },
      { titulo: "Histórico de precios", texto: "Por artículo y proveedor." },
      { titulo: "Optimización de compra", texto: "Qué pedir según lo que se consume." },
    ],
    conexiones: [
      { modulo: "inventario", texto: "La recepción entra en el stock." },
      { modulo: "gastos", texto: "La factura del proveedor se concilia con su albarán." },
      { modulo: "producciones", texto: "El último precio fija el coste de cada receta." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Proveedores de cada día, con el coste del plato al día." },
      { sector: "ecommerce", texto: "Compras de materia prima con su transporte y aranceles." },
    ],
    preguntas: [{ pregunta: "¿Necesito Inventario?", respuesta: "No. Sin Inventario, Compras funciona igual y no mueve stock." }],
  },
  {
    clave: "producciones",
    slug: "produccion",
    nombre: "Producción",
    resumen: "Recetas, escandallos y órdenes de producción con su coste real.",
    titular: "Lo que cuesta de verdad cada cosa que produces.",
    entradilla: "Recetas y escandallos con el último precio de compra, órdenes de producción por fases y un calendario de lo que hay que producir. El coste sale solo.",
    situaciones: [
      { antes: "El escandallo de hace dos años", ahora: "El coste con el último precio de compra", texto: "Cada vez que compras, se actualiza." },
      { antes: "Producir de memoria", ahora: "Órdenes de producción", texto: "Con sus fases, cantidades y lotes." },
      { antes: "Vender por debajo de coste sin saberlo", ahora: "El margen de cada producto", texto: "Con el coste real." },
    ],
    funciones: [
      { titulo: "Recetas y escandallos", texto: "Con bases, mermas y el coste por unidad." },
      { titulo: "Órdenes y fases", texto: "Rutas de producción con sus fases y utensilios." },
      { titulo: "Calendario", texto: "Qué se produce cada día." },
      { titulo: "Etiquetas de lote", texto: "Para la trazabilidad." },
    ],
    conexiones: [
      { modulo: "compras", texto: "El último precio de compra fija el coste." },
      { modulo: "inventario", texto: "Consume ingredientes y da de alta lo producido." },
      { modulo: "workforce", texto: "Las fases usan su catálogo de trabajos." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Recetas, elaboraciones y el coste de cada plato." },
      { sector: "ecommerce", texto: "Lotes y órdenes, con el coste de cada producto." },
    ],
    preguntas: [{ pregunta: "¿Se conecta con NetSuite?", respuesta: "Sí: escandallos, órdenes de producción y costes de compra." }],
  },
  {
    clave: "gastos",
    slug: "gastos",
    nombre: "Gastos",
    resumen: "Facturas recibidas y gastos operativos, conciliados con los albaranes.",
    titular: "Las facturas de proveedor, leídas y conciliadas.",
    entradilla: "Subes la factura y la IA extrae sus datos. Se concilia con su albarán, se clasifica en su categoría y queda lista para el banco.",
    situaciones: [
      { antes: "Teclear cada factura", ahora: "La IA lee la factura", texto: "Proveedor, importes e impuestos." },
      { antes: "Pagar lo que no llegó", ahora: "Factura frente a albarán", texto: "Las diferencias, avisadas." },
      { antes: "Los gastos fijos, en una libreta", ahora: "Gastos operativos", texto: "Alquiler, suministros, comisiones." },
    ],
    funciones: [
      { titulo: "Facturas recibidas", texto: "Con extracción de datos por IA." },
      { titulo: "Gastos operativos", texto: "Lo que no es una compra de mercancía." },
      { titulo: "Categorías", texto: "Para saber a dónde va el dinero." },
      { titulo: "Conciliación con albaranes", texto: "Con Compras." },
    ],
    conexiones: [
      { modulo: "compras", texto: "Cada factura, con su albarán." },
      { modulo: "banca", texto: "Cada pago, con su movimiento." },
      { modulo: "nominas", texto: "La nómina como gasto." },
    ],
    sectores: [{ sector: "hosteleria", texto: "Las comisiones de reparto, como gasto." }],
    preguntas: [{ pregunta: "¿Cómo llegan las facturas?", respuesta: "Las subes y Zindra extrae sus datos." }],
  },
  {
    clave: "banca",
    slug: "banca",
    nombre: "Banca",
    resumen: "Los movimientos del banco, conciliados con facturas, gastos y caja.",
    titular: "Cada movimiento del banco, con su factura.",
    entradilla: "Conectas el banco o importas el Norma 43, y Zindra concilia cada movimiento con lo que tengas activo: cobros, pagos y ventas.",
    situaciones: [
      { antes: "Descargar el extracto cada mes", ahora: "El banco conectado", texto: "Con GoCardless o Norma 43." },
      { antes: "Puntear a mano", ahora: "Conciliación", texto: "Movimientos contra facturas, gastos y caja." },
      { antes: "«¿Nos pagó ese cliente?»", ahora: "El cobro, conciliado", texto: "En la factura." },
    ],
    funciones: [
      { titulo: "Mis bancos", texto: "Todas las cuentas en un sitio." },
      { titulo: "Norma 43 y GoCardless", texto: "Importación o conexión directa." },
      { titulo: "Conciliación", texto: "Contra lo que tengas activo." },
    ],
    conexiones: [
      { modulo: "facturacion", texto: "Cobros de clientes." },
      { modulo: "gastos", texto: "Pagos a proveedores." },
      { modulo: "tickets", texto: "Ventas del TPV." },
    ],
    sectores: [{ sector: "tienda", texto: "El cierre de caja, cuadrado con el banco." }],
    preguntas: [{ pregunta: "¿Sin otros módulos sirve?", respuesta: "Sí, enseña los extractos. Con Facturación o Gastos, concilia." }],
  },

  // ── Ventas ──────────────────────────────────────────────────────────────
  {
    clave: "facturacion",
    slug: "facturacion",
    nombre: "Facturación",
    resumen: "Facturas, presupuestos, albaranes y recurrentes, con su enlace para el cliente.",
    titular: "Facturas, presupuestos y albaranes, en un minuto.",
    entradilla: "Con tus series, tus tarifas y tu PDF. El cliente recibe un enlace, los recordatorios salen solos y las recurrentes se generan cada mes.",
    situaciones: [
      { antes: "La plantilla de Word", ahora: "Facturas con tus series", texto: "Y tu diseño de PDF." },
      { antes: "Perseguir cobros", ahora: "Recordatorios automáticos", texto: "Y las vencidas, marcadas." },
      { antes: "Hacer la misma factura cada mes", ahora: "Facturas recurrentes", texto: "Que se generan solas." },
    ],
    funciones: [
      { titulo: "Facturas y presupuestos", texto: "Con enlace propio para el cliente." },
      { titulo: "Albaranes de cliente", texto: "Con su vista previa y sus imágenes." },
      { titulo: "Tarifas", texto: "Precios por cliente o canal." },
      { titulo: "Ventas por canal", texto: "Cuánto vende cada canal." },
    ],
    conexiones: [
      { modulo: "inventario", texto: "Lo facturado se descuenta del stock." },
      { modulo: "banca", texto: "Cada cobro, conciliado." },
      { modulo: "tickets", texto: "El resumen de caja es un documento de facturación." },
    ],
    sectores: [
      { sector: "servicios", texto: "Presupuestos y facturas recurrentes a clientes." },
      { sector: "ecommerce", texto: "Mayoristas y distribución, con sus tarifas." },
    ],
    preguntas: [{ pregunta: "¿Se conecta con Holded o Shopify?", respuesta: "Sí, los dos escriben en Facturación. De Holded se importan clientes, productos, facturas y compras." }],
  },
  {
    clave: "tickets",
    slug: "caja",
    nombre: "Caja y tickets",
    resumen: "Los tickets de Square, el resumen del día y el cierre de caja.",
    titular: "El cierre de caja, cuadrado y sin papeles.",
    entradilla: "Los tickets de Square entran solos, cada día sale su resumen y el cierre de caja se cuadra por forma de pago.",
    requiere: "facturacion",
    situaciones: [
      { antes: "El arqueo en un papel", ahora: "Cierre de caja en Zindra", texto: "Por forma de pago." },
      { antes: "Pasar las ventas del TPV a mano", ahora: "Los tickets entran solos", texto: "Con Square." },
      { antes: "El resumen del día, al día siguiente", ahora: "El resumen del día", texto: "Como documento fiscal." },
    ],
    funciones: [
      { titulo: "Tickets", texto: "Cada venta de Square." },
      { titulo: "Resumen diario", texto: "Con sus series e impuestos." },
      { titulo: "Cierre de caja", texto: "Lo que hay frente a lo que debería haber." },
    ],
    conexiones: [
      { modulo: "inventario", texto: "Lo vendido se descuenta del stock." },
      { modulo: "cartas_pedidos", texto: "Los cobros de mesa llegan a caja." },
      { modulo: "banca", texto: "Las ventas, conciliadas con el banco." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Cierre de cada servicio y de cada local." },
      { sector: "tienda", texto: "La caja de cada tienda, cuadrada cada noche." },
    ],
    preguntas: [{ pregunta: "¿Por qué necesita Facturación?", respuesta: "El resumen de caja es un documento fiscal, con las series e impuestos de Facturación." }],
  },
  {
    clave: "cartas_pedidos",
    slug: "sala-y-cartas",
    nombre: "Sala y cartas",
    resumen: "Cartas digitales, pedidos en mesa, pantallas de cocina y barra, y reparto.",
    titular: "Del pedido en la mesa a la pantalla de cocina, sin comandas de papel.",
    entradilla: "Tu carta digital con QR, pedidos en mesa, pantallas para cocina y barra, y los pedidos de reparto en el mismo sitio.",
    situaciones: [
      { antes: "Comandas a mano", ahora: "Pedidos en mesa", texto: "Desde la carta o el camarero." },
      { antes: "Gritar en la cocina", ahora: "Pantallas de cocina y barra", texto: "Cada plato, en su sitio." },
      { antes: "Tres tablets de reparto", ahora: "El reparto, con lo demás", texto: "Glovo en las mismas pantallas." },
    ],
    funciones: [
      { titulo: "Cartas", texto: "Con su QR para cada mesa." },
      { titulo: "Pedidos y mesas", texto: "El estado de cada mesa." },
      { titulo: "Cocina, barra y kanban", texto: "Pantallas para cada zona." },
      { titulo: "Reparto", texto: "Pedidos de plataformas." },
    ],
    conexiones: [
      { modulo: "tickets", texto: "El cobro de la mesa llega a caja." },
      { modulo: "producciones", texto: "Las recetas, en la carta." },
      { modulo: "reservas", texto: "La ocupación de la sala." },
    ],
    sectores: [{ sector: "hosteleria", texto: "Bares, restaurantes y cafeterías, con una o varias salas." }],
    preguntas: [{ pregunta: "¿La carta digital es gratis?", respuesta: "La carta y la ficha en la guía de restaurantes no se cobran; los pedidos y pantallas son este módulo." }],
  },
  {
    clave: "reservas",
    slug: "reservas",
    nombre: "Reservas",
    resumen: "Reservas de mesa desde tu carta, con recordatorios.",
    titular: "Reservas desde tu carta, con recordatorio y sin libreta.",
    entradilla: "El cliente reserva desde tu carta digital, recibe su recordatorio y puede gestionar su reserva. Tú ves la ocupación de cada servicio.",
    situaciones: [
      { antes: "La libreta junto al teléfono", ahora: "Reservas en Zindra", texto: "Desde la carta o a mano." },
      { antes: "Mesas vacías por olvidos", ahora: "Recordatorios", texto: "Antes de cada reserva." },
      { antes: "«¿Cabemos esta noche?»", ahora: "La ocupación del servicio", texto: "De un vistazo." },
    ],
    funciones: [
      { titulo: "Reservas", texto: "Con horarios y capacidad." },
      { titulo: "Recordatorios", texto: "Automáticos al cliente." },
      { titulo: "Gestión por el cliente", texto: "Un enlace para ver o cambiar su reserva." },
    ],
    conexiones: [{ modulo: "cartas_pedidos", texto: "La ocupación de la sala." }],
    sectores: [{ sector: "hosteleria", texto: "Restaurantes con servicio de comidas y cenas." }],
    preguntas: [{ pregunta: "¿Necesito Sala y cartas?", respuesta: "No, pero juntos se ve la ocupación de la sala." }],
  },

  // ── Dirección ───────────────────────────────────────────────────────────
  {
    clave: "informes",
    slug: "informes",
    nombre: "Informes",
    resumen: "Ventas, márgenes y equipo en un panel.",
    titular: "Cómo va tu negocio, sin montar un Excel.",
    entradilla: "Informes de negocio y de equipo con lo que tengas activo: ventas, márgenes por canal, coste de personal, horas. Y los que uses a menudo, guardados.",
    situaciones: [
      { antes: "Exportar de tres sitios", ahora: "Un panel con todo", texto: "De los módulos que tengas." },
      { antes: "El margen, a final de trimestre", ahora: "El margen por canal", texto: "Con el coste real." },
      { antes: "Repetir el mismo informe cada lunes", ahora: "Informes guardados", texto: "Listos cuando los abras." },
    ],
    funciones: [
      { titulo: "Informes de negocio", texto: "Ventas, gastos y resultado." },
      { titulo: "Informes de equipo", texto: "Horas, ausencias y coste." },
      { titulo: "Analítica", texto: "Por centro, canal y periodo." },
      { titulo: "Guardados", texto: "Los tuyos, a un clic." },
    ],
    conexiones: [
      { modulo: "facturacion", texto: "Ventas y cobros." },
      { modulo: "fichaje", texto: "Horas trabajadas." },
      { modulo: "producciones", texto: "Coste y margen de cada producto." },
    ],
    sectores: [{ sector: "ecommerce", texto: "Margen por canal: online, marketplaces, distribución." }],
    preguntas: [{ pregunta: "¿Qué informes salen?", respuesta: "Los de los módulos que tengas activos: cada bloque necesita su fuente." }],
  },
  {
    clave: "chat",
    slug: "chat",
    nombre: "Chat",
    resumen: "Canales y mensajes directos, con hilos, menciones, avisos al móvil y agentes.",
    titular: "El chat de tu equipo, con agentes que conocen tu empresa.",
    entradilla: "Canales y mensajes directos para todo el equipo, también quien no tiene ordenador. Y agentes a los que preguntar por horas, vacaciones o ventas, que miran con tus permisos.",
    situaciones: [
      { antes: "El grupo de WhatsApp de la empresa", ahora: "Canales por equipo o proyecto", texto: "Con hilos y menciones." },
      { antes: "Buscar el dato en tres sitios", ahora: "Se lo preguntas al agente", texto: "Con tus datos y tus permisos." },
      { antes: "Llamadas sin acta", ahora: "Llamadas con notas", texto: "Dentro del canal." },
    ],
    funciones: [
      { titulo: "Canales y directos", texto: "Con hilos, menciones y reacciones." },
      { titulo: "Agentes", texto: "Preguntas sobre tu empresa, contestadas con tus datos." },
      { titulo: "Llamadas", texto: "Con notas que se guardan en el canal." },
      { titulo: "En todas partes", texto: "Web, Mac, Windows, iPhone y Android, con avisos." },
    ],
    conexiones: [
      { modulo: "proyectos", texto: "Los proyectos viven dentro del chat." },
      { modulo: "informes", texto: "El agente responde con los datos de los módulos activos." },
    ],
    sectores: [
      { sector: "servicios", texto: "Un canal por cliente, con el proyecto al lado." },
      { sector: "saas", texto: "El equipo en remoto, en una sola app." },
    ],
    preguntas: [{ pregunta: "¿Qué puede ver el agente?", respuesta: "Lo que puede ver quien le pregunta, nunca más, y solo de los módulos activos." }],
  },
  {
    clave: "proyectos",
    slug: "proyectos",
    nombre: "Proyectos",
    resumen: "Proyectos con tareas y subtareas, en lista, tablero y calendario.",
    titular: "Proyectos y tareas, en el mismo sitio donde habla tu equipo.",
    entradilla: "Proyectos con tareas y subtareas en lista, tablero o calendario, con sus columnas y estados. Y si vienes de Asana, se traen solos.",
    situaciones: [
      { antes: "Tareas en el chat que se pierden", ahora: "Cada tarea en su proyecto", texto: "Con responsable y fecha." },
      { antes: "Una herramienta más que pagar", ahora: "Dentro de Zindra Work", texto: "Con el chat al lado." },
      { antes: "Migrar desde Asana a mano", ahora: "Importación de Asana", texto: "Tareas, adjuntos, comentarios y estados." },
    ],
    funciones: [
      { titulo: "Lista, tablero y calendario", texto: "La misma tarea, como quieras verla." },
      { titulo: "Columnas y estados", texto: "Los tuyos." },
      { titulo: "Subtareas y adjuntos", texto: "Con comentarios." },
      { titulo: "Importación de Asana", texto: "Y lo nuevo sigue llegando mientras convivís." },
    ],
    conexiones: [{ modulo: "chat", texto: "Los proyectos se abren dentro del chat." }],
    sectores: [
      { sector: "servicios", texto: "Proyectos por cliente, con sus entregas." },
      { sector: "saas", texto: "Sprints en el tablero." },
    ],
    preguntas: [{ pregunta: "¿Puedo traer mis proyectos de Asana?", respuesta: "Sí, con sus tareas, adjuntos, comentarios y estados." }],
  },
]

export function fichaPorSlug(slug: string): FichaDeModulo | undefined {
  return MODULOS.find((m) => m.slug === slug)
}

export function fichaPorClave(clave: ModuloVendible): FichaDeModulo {
  const f = MODULOS.find((m) => m.clave === clave)
  if (!f) throw new Error(`Sin ficha de landing para ${clave}`)
  return f
}
